import { useEffect, useState } from 'react';
import {
  shiftDisplayStatus,
  type ShiftListScope,
  type ShiftListStatusFilter,
} from '@truecare/schemas';
import { DateInput, StatusBadge } from '@truecare/ui';

/**
 * Doc 17 — the one set of list controls for every "my shifts" list (facility +
 * homecare Booked shifts/visits, care professional Schedule):
 *
 *   [Upcoming · Past · Date range]   [status ▾]
 *   From [____]  To [____]            (Date range only)
 *
 * Upcoming / Past split on the shift's scheduled END (A1). Order is fixed per
 * view (A6), so there is no sort control. Any change resets to page 1 (A8).
 */

const SCOPES: { value: ShiftListScope; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'range', label: 'Date range' },
];

const STATUS_OPTIONS: { value: ShiftListStatusFilter | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'NEEDS_SIGNOFF', label: 'Needs sign-off' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'NO_SHOW', label: 'No-show' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'DISPUTED', label: 'Disputed' },
];

export const SHIFT_LIST_PAGE_SIZE = 20;

/** A `YYYY-MM-DD` date as the viewer's local midnight, plus `addDays` (A3). */
function localMidnightIso(date: string, addDays = 0): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d! + addDays).toISOString();
}

export function useShiftListState() {
  const [scope, setScopeRaw] = useState<ShiftListScope>('upcoming');
  const [status, setStatusRaw] = useState<ShiftListStatusFilter | ''>('');
  const [fromDate, setFromRaw] = useState('');
  const [toDate, setToRaw] = useState('');
  const [page, setPage] = useState(1);

  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  const rangeInvalid = scope === 'range' && fromDate !== '' && toDate !== '' && toDate < fromDate;
  // A range only queries once both dates are complete and in order.
  const ready = scope !== 'range' || (fromDate !== '' && toDate !== '' && !rangeInvalid);

  const query = {
    scope,
    ...(status ? { status } : {}),
    ...(scope === 'range' && ready
      ? { from: localMidnightIso(fromDate), to: localMidnightIso(toDate, 1) }
      : {}),
    page,
    pageSize: SHIFT_LIST_PAGE_SIZE,
  };

  return {
    scope,
    setScope: reset(setScopeRaw),
    status,
    setStatus: reset(setStatusRaw),
    fromDate,
    setFromDate: reset(setFromRaw),
    toDate,
    setToDate: reset(setToRaw),
    page,
    setPage,
    rangeInvalid,
    ready,
    query,
  };
}

export type ShiftListState = ReturnType<typeof useShiftListState>;

/**
 * Labels for the derived NEEDS_SIGNOFF status (A5), by who acts: the buyer signs
 * ("Needs sign-off"); the care professional waits on them ("Awaiting sign-off").
 * Doc 18 replaces this flag with a real state.
 */
export const SIGNOFF_LABEL = { buyer: 'Needs sign-off', caregiver: 'Awaiting sign-off' } as const;

export function ShiftListControls({
  state,
  signoffLabel = SIGNOFF_LABEL.buyer,
}: {
  state: ShiftListState;
  signoffLabel?: string;
}) {
  const selectClass =
    'rounded-md border border-ink-soft bg-paper px-2 py-1 font-body text-sm text-ink';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Which shifts to show"
          className="inline-flex rounded-md border border-ink-soft p-0.5"
        >
          {SCOPES.map((s) => {
            const active = state.scope === s.value;
            return (
              <button
                key={s.value}
                type="button"
                aria-pressed={active}
                onClick={() => state.setScope(s.value)}
                className={
                  'rounded px-3 py-1 font-body text-sm ' +
                  (active ? 'bg-forest text-paper' : 'text-forest hover:bg-forest/10')
                }
                data-testid={`shift-scope-${s.value}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <label className="font-body text-sm text-ink-muted">
          <span className="sr-only">Filter by status</span>
          <select
            className={selectClass}
            value={state.status}
            onChange={(e) => state.setStatus(e.target.value as ShiftListStatusFilter | '')}
            data-testid="shift-status-filter"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.value === 'NEEDS_SIGNOFF' ? signoffLabel : o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {state.scope === 'range' && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 font-body text-sm text-ink-muted">
            From
            <DateInput
              value={state.fromDate}
              onChange={state.setFromDate}
              aria-invalid={state.rangeInvalid || undefined}
              data-testid="shift-range-from"
            />
          </label>
          <label className="flex flex-col gap-1 font-body text-sm text-ink-muted">
            To
            <DateInput
              value={state.toDate}
              onChange={state.setToDate}
              aria-invalid={state.rangeInvalid || undefined}
              data-testid="shift-range-to"
            />
          </label>
          {state.rangeInvalid && (
            <p role="alert" className="font-body text-sm text-danger">
              The end date must be on or after the start date.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Keep `page` in range when the list shrinks under the viewer (a shift ends,
 * is cancelled, a payout is voided). <Pager> hides itself at one page, so
 * without this someone on page 2 is left on an empty list with no way back.
 * Skips placeholder data (the previous page shown while the next one loads).
 */
export function usePageClamp(
  page: number,
  setPage: (p: number) => void,
  pageSize: number,
  totalCount: number | undefined,
  isPlaceholderData = false,
) {
  useEffect(() => {
    if (totalCount === undefined || isPlaceholderData) return;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [page, setPage, pageSize, totalCount, isPlaceholderData]);
}

/**
 * What to say when the list is empty, so the reason is clear. Null while the
 * range is invalid: the range error already explains it.
 */
export function emptyShiftListMessage(state: ShiftListState, noun: string): string | null {
  if (state.rangeInvalid) return null;
  if (state.scope === 'range' && !state.ready) return `Choose a start and end date to see ${noun}s.`;
  if (state.status) return `No ${noun}s match this filter.`;
  if (state.scope === 'upcoming') return `No upcoming ${noun}s.`;
  if (state.scope === 'past') return `No past ${noun}s yet.`;
  return `No ${noun}s in these dates.`;
}

/** Status badge with the derived sign-off status (A5). */
export function ShiftStatusBadge({
  status,
  scheduledEnd,
  signoffLabel = SIGNOFF_LABEL.buyer,
}: {
  status: string;
  scheduledEnd: string;
  signoffLabel?: string;
}) {
  const shown = shiftDisplayStatus({ status, scheduledEnd });
  if (shown === 'NEEDS_SIGNOFF') {
    return <StatusBadge status={shown} tone="warning" label={signoffLabel} />;
  }
  return <StatusBadge status={shown} />;
}

export function Pager({
  page,
  pageSize,
  totalCount,
  onPage,
  noun = 'shift',
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPage: (p: number) => void;
  noun?: string;
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (totalPages <= 1) return null;
  const btn =
    'px-3 py-1 rounded-md border border-ink-soft font-body text-sm text-forest disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center justify-between gap-3">
      <button type="button" className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Previous
      </button>
      <span className="font-body text-sm text-ink-muted">
        Page {page} of {totalPages} · {totalCount} {noun}
        {totalCount === 1 ? '' : 's'}
      </span>
      <button
        type="button"
        className={btn}
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Next →
      </button>
    </div>
  );
}
